/** @format */
import { Injectable } from '@angular/core';
import { Education, LearningOpportunity, LocalizedString, OtmId, Plan } from 'common-typescript/types';
import { LocaleService } from 'sis-common';
import { uniqueBy } from 'sis-components';

/**
 * Represents selection options in a dropdown menu. Items with the education property set represent group headers
 * (the plans are grouped by education), and items with the plan property set are the actual selectable items.
 */
export interface PlanOption {
    id: OtmId;
    education?: Education;
    plan?: Plan;
    selectedLearningOpportunityName?: LocalizedString;
}

@Injectable({ providedIn: 'root' })
export class PlanSelectService {
    constructor(private localeService: LocaleService) {}

    /**
     * Creates an option for each education in {@link planEducations} having plans in {@link plans} (referred by {@link Plan.rootId}),
     * and for each plan in {@link plans} having an education in {@link planEducations}.
     */
    createSelectOptionsFromPlansAndEducations(plans: readonly Plan[], planEducations: readonly Education[]): PlanOption[] {
        const currentLocale: string = this.localeService.getCurrentLanguage();
        const plansByEducationId: Partial<Record<OtmId, Plan[]>> = Object.groupBy(
            plans.filter(plan => !!plan.rootId),
            plan => plan.rootId,
        );
        const educations: Education[] = uniqueBy(planEducations, education => education.id)
            .filter(education => !!plansByEducationId[education.id])
            .map(education => ({ education, translatedName: education.name?.[currentLocale] }))
            .sort((a, b) => compareByTranslatedName(a, b, currentLocale))
            .map(educationAndTranslatedName => educationAndTranslatedName.education);

        const options: PlanOption[] = [];
        educations.forEach(education => {
            options.push({ education, id: education.id });
            options.push(
                ...plansByEducationId[education.id].map(plan => ({
                    plan,
                    id: plan.id,
                    selectedLearningOpportunityName: PlanSelectService.getSelectedLearningOpportunityName(plan, education),
                })),
            );
        });

        return options;
    }

    private static getSelectedLearningOpportunityName(plan: Plan, education: Education | undefined): LocalizedString | undefined {
        return education?.structure?.learningOpportunities?.find((lo: LearningOpportunity) => lo.localId === plan.learningOpportunityId)
            ?.name;
    }
}

function compareByTranslatedName(
    a: { translatedName: string | undefined },
    b: { translatedName: string | undefined },
    currentLocale: string,
): number {
    return a.translatedName
        ? b.translatedName
            ? a.translatedName.localeCompare(b.translatedName, currentLocale)
            : -1 // name comes before no-name
        : b.translatedName
          ? 1
          : 0;
}
